import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FOUNDING_OWNER_ID } from '../../utils/constants';
import api from '../../api';

export default function PartnersPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ discord_id: '', username: '', tier: 'partner' });
  const [adding, setAdding] = useState(false);

  const isOwner = user?.discord_id === FOUNDING_OWNER_ID;
  const isPartner = !isOwner && (user?.bot_tier === 'partner' || user?.bot_tier === 'owner');
  const hasAccess = isOwner || isPartner;

  useEffect(() => {
    if (!hasAccess) return;
    api.get('/api/partners')
      .then(res => setPartners(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isOwner]);

  const handleAdd = async () => {
    if (!form.discord_id.trim()) return alert('Discord ID required.');
    setAdding(true);
    try {
      const res = await api.post('/api/partners', form);
      setPartners(prev => [res.data, ...prev]);
      setForm({ discord_id: '', username: '', tier: 'partner' });
    } catch { alert('Failed to add partner.'); }
    finally { setAdding(false); }
  };

  const handleRemove = async (discordId) => {
    if (!window.confirm('Remove this partner?')) return;
    try {
      await api.delete(`/api/partners/${discordId}`);
      setPartners(prev => prev.filter(p => p.discord_id !== discordId));
    } catch { alert('Failed to remove partner.'); }
  };

  if (!hasAccess) return (
    <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔒</div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Access Restricted</div>
      <div>This section is restricted to owners and partners.</div>
    </div>
  );

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Partners</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>{isOwner ? 'Partners can generate license keys for other servers' : 'View the owner and partner list'}</p>
      </div>

      {/* Add partner form — owner only */}
      {isOwner && <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)', marginBottom: '24px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-primary)', marginBottom: '16px' }}>Add Partner</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: '12px' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Discord ID</label>
            <input type="text" placeholder="123456789012345678"
              value={form.discord_id} onChange={e => setForm(p => ({ ...p, discord_id: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Username (optional)</label>
            <input type="text" placeholder="Username"
              value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Tier</label>
            <select value={form.tier} onChange={e => setForm(p => ({ ...p, tier: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
              <option value="partner">Partner</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>
        <button onClick={handleAdd} disabled={adding} style={{
          marginTop: '14px', padding: '10px 24px', background: 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px',
          fontWeight: 700, cursor: adding ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-display)',
        }}>
          {adding ? 'Adding...' : '+ Add Partner'}
        </button>
      </div>}

      {/* Partners list — read-only for partners */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading...</div>
      ) : partners.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No partners yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {partners.map(p => (
            <div key={p.discord_id} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px',
              padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px',
              boxShadow: 'var(--shadow-card)',
            }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
                background: 'var(--gradient-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', fontWeight: 700, color: '#fff',
              }}>
                {(p.username || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {p.username || 'Unknown'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  ID: {p.discord_id} · Added {new Date(p.created_at).toLocaleDateString()}
                </div>
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '100px',
                background: p.tier === 'owner' ? 'var(--accent-secondary)20' : 'var(--accent-soft)',
                color: p.tier === 'owner' ? 'var(--accent-secondary)' : 'var(--accent-primary)',
              }}>
                {p.tier === 'owner' ? '👑 Owner' : '🤝 Partner'}
              </span>
              {isOwner && <button onClick={() => handleRemove(p.discord_id)} style={{
                padding: '7px 14px', borderRadius: '8px', cursor: 'pointer',
                border: '1px solid var(--danger)', background: 'transparent',
                fontSize: '12px', color: 'var(--danger)', fontFamily: 'var(--font-body)',
              }}>Remove</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
